import { Box, Button, Flex, Text } from "@chakra-ui/react";

import React, { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

const segments = [
  {
    title: "INCOME",
    subtitle: "CALIBRATE CAPACITY",
    desc: "Understand how much your business can deliver – and at what value.",
    gradientStart: "#EDF2F7",
    gradientEnd: "#718096",
    hoverStart: "#00B5D8",
    hoverEnd: "#065666",
    route: "/lucro-metrics/income/",
  },
  {
    title: "TEAM",
    subtitle: "POSITION PEOPLE",
    desc: "Build and align a team that can meet your capacity goals efficiently.",
    gradientStart: "#EDF2F7",
    gradientEnd: "#718096",
    hoverStart: "#FFC45B",
    hoverEnd: "#E65B00",
    route: "/lucro-metrics/team/",
  },
  {
    title: "MARKETING",
    subtitle: "DRIVE DEMAND",
    desc: "Create consistent patient flow through all systems.",
    gradientStart: "#EDF2F7",
    gradientEnd: "#718096",
    hoverStart: "#627CFB",
    hoverEnd: "#233DBC",
    route: "/lucro-metrics/marketing/",
  },
  {
    title: "OPERATIONS",
    subtitle: "OPTIMIZE OPERATIONS",
    desc: "Streamline processes, systems, and logistics so the business runs efficiently.",
    gradientStart: "#EDF2F7",
    gradientEnd: "#718096",
    hoverStart: "#38B2AC",
    hoverEnd: "#00413E",
    route: "/lucro-metrics/overhead/",
  },
  {
    title: "PRODUCTS",
    subtitle: "SCALE SUSTAINABLY",
    desc: "Expand intelligently: growth should strengthen, not strain, the business.",
    gradientStart: "#EDF2F7",
    gradientEnd: "#718096",
    hoverStart: "#EF5BA2",
    hoverEnd: "#702459",
    route: "/lucro-metrics/cogs/",
  },
];

export default function OperationalWheel({
  onSegmentClick,
  drillDownEnabled = true,
}) {
  const containerRef = useRef(null);
  const [size, setSize] = useState(685);
  const navigate = useNavigate();
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const hoverOffset = 6;

  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      const maxWidth = containerRef.current.offsetWidth;
      setSize(Math.min(685, maxWidth));
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const radius = size / 2;
  const center = radius;
  const segmentCount = segments.length;
  const segmentAngle = 360 / segmentCount;
  const innerLineWidth = size * 0.026;
  const angleOffset = -90 - segmentAngle / 2;

  const polarToCartesian = (cx, cy, r, angle) => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return `M${cx},${cy} L${start.x},${start.y} A${r},${r} 0 ${largeArcFlag},0 ${end.x},${end.y} Z`;
  };

  return (
    <Box
      ref={containerRef}
      width="100%"
      maxW="100vw"
      height={size}
      position="relative"
    >
      <Flex
        position="absolute"
        top="50%"
        left="50%"
        width={size}
        height={size}
        transform="translate(-50%, -50%)"
      >
        <svg width={size} height={size} style={{ overflow: "visible" }}>
          <defs>
            {segments.map((seg, idx) => {
              const startAngle = idx * segmentAngle + angleOffset;
              const endAngle = (idx + 1) * segmentAngle + angleOffset;
              const midAngle = startAngle + (endAngle - startAngle) / 2;

              const shadowDistance = size * 0.005;
              const sideDistance = size * 0.005;
              const outwardDx =
                Math.cos((midAngle * Math.PI) / 180) * shadowDistance;
              const outwardDy =
                Math.sin((midAngle * Math.PI) / 180) * shadowDistance;

              const perpAngle = midAngle + 90;
              const sideDx =
                Math.cos((perpAngle * Math.PI) / 180) * sideDistance;
              const sideDy =
                Math.sin((perpAngle * Math.PI) / 180) * sideDistance;

              return (
                <React.Fragment key={`defs-${idx}`}>
                  <linearGradient
                    id={`grad-${idx}`}
                    x1="0%"
                    y1="0%"
                    x2="75%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor={seg.gradientStart} />
                    <stop offset="68%" stopColor={seg.gradientEnd} />
                    <stop offset="100%" stopColor={seg.gradientEnd} />
                  </linearGradient>

                  <linearGradient
                    id={`grad-hover-${idx}`}
                    x1="0%"
                    y1="0%"
                    x2="75%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor={seg.hoverStart} />
                    <stop offset="68%" stopColor={seg.hoverEnd} />
                    <stop offset="100%" stopColor={seg.hoverEnd} />
                  </linearGradient>

                  <filter
                    id={`segShadow-${idx}`}
                    x="-80%"
                    y="-80%"
                    width="260%"
                    height="260%"
                    colorInterpolationFilters="sRGB"
                  >
                    <feDropShadow
                      dx={outwardDx}
                      dy={outwardDy}
                      stdDeviation={6}
                      floodColor="rgba(0,0,0,0.28)"
                    />

                    <feDropShadow
                      dx={sideDx}
                      dy={sideDy}
                      stdDeviation={4}
                      floodColor="rgba(0,0,0,0.18)"
                    />

                    <feDropShadow
                      dx={-sideDx}
                      dy={-sideDy}
                      stdDeviation={4}
                      floodColor="rgba(0,0,0,0.14)"
                    />

                    <feGaussianBlur stdDeviation="0.2" />
                  </filter>
                </React.Fragment>
              );
            })}

            <linearGradient
              id="innerLineGrad"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="-12.6%" stopColor="rgba(255,255,255,0.2)" />
              <stop offset="124.47%" stopColor="rgba(255,255,255,0.2)" />
            </linearGradient>
          </defs>

          <circle cx={center} cy={center} r={radius + 0.8} fill="#e8ebef" />

          {segments.map((seg, idx) => {
            const isHovered = hoveredSegment === idx;

            const startAngle = idx * segmentAngle + angleOffset;
            const endAngle = (idx + 1) * segmentAngle + angleOffset;

            const currentRadius = isHovered ? radius + hoverOffset : radius;
            const path = describeArc(
              center,
              center,
              currentRadius,
              startAngle,
              endAngle
            );

            return (
              <g
                key={`seg-${idx}`}
                onMouseEnter={() => setHoveredSegment(idx)}
                onMouseLeave={() => setHoveredSegment(null)}
                onClick={() => onSegmentClick(seg)}
              >
                <path
                  d={path}
                  fill={
                    isHovered ? `url(#grad-hover-${idx})` : `url(#grad-${idx})`
                  }
                  filter={isHovered ? `url(#segShadow-${idx})` : "none"}
                  style={{
                    cursor: "pointer",
                    transition:
                      "d 0.22s cubic-bezier(.2,.9,.2,1), filter 0.22s ease",
                    vectorEffect: "non-scaling-stroke",
                  }}
                />
              </g>
            );
          })}

          <circle
            cx={center}
            cy={center}
            r={radius - innerLineWidth / 2}
            fill="none"
            stroke="url(#innerLineGrad)"
            strokeWidth={innerLineWidth}
          />
        </svg>

        <svg
          width={size}
          height={size}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="4"
              markerHeight="4"
              refX="2"
              refY="2"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,4 L4,2 z" fill="#A0AEC0" />
            </marker>
          </defs>

          {segments.map((seg, idx) => {
            const nextIdx = (idx + 1) % segments.length;
            const arrowRadius = radius * 1.09;
            const gapAngle = segmentAngle * 0.21;

            let startAngle =
              idx * segmentAngle + segmentAngle / 2 + angleOffset + gapAngle;
            let endAngle =
              nextIdx * segmentAngle +
              segmentAngle / 2 +
              angleOffset -
              gapAngle;

            startAngle = (startAngle + 360) % 360;
            endAngle = (endAngle + 360) % 360;

            const x1 =
              center + arrowRadius * Math.cos((startAngle * Math.PI) / 180);
            const y1 =
              center + arrowRadius * Math.sin((startAngle * Math.PI) / 180);
            const x2 =
              center + arrowRadius * Math.cos((endAngle * Math.PI) / 180);
            const y2 =
              center + arrowRadius * Math.sin((endAngle * Math.PI) / 180);

            const deltaAngle = (endAngle - startAngle + 360) % 360;
            const largeArcFlag = deltaAngle > 180 ? 1 : 0;

            const pathD = `M${x1},${y1} A${arrowRadius},${arrowRadius} 0 ${largeArcFlag} 1 ${x2},${y2}`;

            return (
              <path
                key={idx}
                d={pathD}
                fill="none"
                stroke="#A0AEC0"
                strokeWidth={size * 0.01}
                strokeLinecap="round"
                strokeLinejoin="round"
                markerEnd="url(#arrowhead)"
              />
            );
          })}
        </svg>

        <Flex
          position="absolute"
          top={center}
          left={center}
          transform="translate(-50%, -50%)"
          align="center"
          justify="center"
        >
          <Box
            position="absolute"
            width={size * 0.5}
            height={size * 0.5}
            borderRadius="full"
            bg="#F4F4F2"
            zIndex={0}
          />
          <Box
            position="absolute"
            width={size * 0.33}
            height={size * 0.33}
            borderRadius="full"
            bg="radial-gradient(50% 50% at 50% 50%, #000 40.54%, rgba(3,3,3,0.83) 50.62%, rgba(13,13,13,0.7) 58.58%, rgba(30,30,30,0.57) 65.82%, rgba(53,53,53,0.46) 72.64%, rgba(84,84,84,0.35) 79.15%, rgba(121,121,121,0.245) 85.43%, rgba(166,166,166,0.142) 91.54%, rgba(216,216,216,0.045) 97.36%, rgba(241,241,241,0) 100%)"
            zIndex={0}
          />
          <Box
            position="absolute"
            width={size * 0.31}
            height={size * 0.31}
            borderRadius="full"
            bg={"white"}
            zIndex={0}
          />
          <Box
            position="absolute"
            width={size * 0.31}
            height={size * 0.31}
            borderRadius="full"
            bg="radial-gradient(50% 50% at 50% 50%, #000 40.54%, rgba(3,3,3,0.83) 50.62%, rgba(13,13,13,0.7) 58.58%, rgba(30,30,30,0.57) 65.82%, rgba(53,53,53,0.46) 72.64%, rgba(84,84,84,0.35) 79.15%, rgba(121,121,121,0.245) 85.43%, rgba(166,166,166,0.142) 91.54%, rgba(216,216,216,0.045) 97.36%, rgba(241,241,241,0) 100%)"
            zIndex={1}
          />
          <Flex
            role="group"
            position="absolute"
            width={size * 0.25}
            height={size * 0.25}
            borderRadius="full"
            align="center"
            justify="center"
            zIndex={2}
            bg="linear-gradient(215.45deg, #E4E3E8 16.8%, #FFFFFF 83.95%)"
            cursor={drillDownEnabled ? "pointer" : "default"}
            transition="all 0.3s ease"
            _hover={
              drillDownEnabled
                ? {
                    bg: "linear-gradient(215.45deg, #22543D 16.8%, #38A169 83.95%)",
                  }
                : undefined
            }
            onClick={
              drillDownEnabled ? () => navigate("/lucro-metrics/profit/") : undefined
            }
          >
            <Text
              fontSize={`clamp(${size * 0.03}px, 3vw, ${size * 0.05}px)`}
              fontWeight="bold"
              fontFamily="Poppins"
              color="black"
              transition="color 0.3s ease"
              _groupHover={drillDownEnabled ? { color: "white" } : undefined}
            >
              PROFIT
            </Text>
          </Flex>
        </Flex>

        {segments.map((seg, idx) => {
          const angle = idx * segmentAngle + segmentAngle / 2 + angleOffset;
          const angleRad = (angle * Math.PI) / 180;
          const x = center + radius * Math.cos(angleRad);
          const y = center + radius * Math.sin(angleRad);

          let rotation = angle + 90;
          if (rotation > 90 && rotation < 270) rotation -= 180;
          if (rotation < -90 && rotation > -270) rotation += 180;

          const pillWidth = Math.max(size * 0.22, 90);
          const pillHeight = Math.max(size * 0.07, 35);

          const textRadius = radius * 0.72;
          const xText = center + textRadius * Math.cos(angleRad);
          const yText = center + textRadius * Math.sin(angleRad);

          const outerTextRingRadius = (size * 0.4) / 2;
          const overlapAmount = size * 0.02;
          const indicatorRadius = outerTextRingRadius + overlapAmount;
          const xInd = center + indicatorRadius * Math.cos(angleRad);
          const yInd = center + indicatorRadius * Math.sin(angleRad);

          return (
            <React.Fragment key={idx}>
              {drillDownEnabled && (
                <Box
                  position="absolute"
                  left={x}
                  top={y}
                  transform={`translate(-50%, -50%) rotate(${rotation}deg)`}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  role="group"
                  cursor="pointer"
                  onClick={() => navigate(seg.route)}
                >
                  <Box
                    position="absolute"
                    width={pillWidth}
                    height={pillHeight + 4}
                    borderRadius="full"
                    bg="#F4F4F2"
                    zIndex={0}
                    transition="box-shadow 0.25s ease"
                    _groupHover={{
                      boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    }}
                  />

                  <Button
                    width={pillWidth * 0.93}
                    height={pillHeight * 0.85}
                    fontSize={`clamp(${size * 0.02}px, 2.5vw, ${size * 0.03}px)`}
                    borderRadius="full"
                    fontWeight={600}
                    color="#F9F9F9"
                    px={4}
                    fontFamily="Poppins"
                    zIndex={1}
                    bg="linear-gradient(180deg, #CBD5E0 -19.61%, #718096 131.76%)"
                    _groupHover={{
                      bg: `linear-gradient(180deg, ${seg.hoverStart} -19.61%, ${seg.hoverEnd} 131.76%)`,
                    }}
                    _active={{
                      bg: `linear-gradient(180deg, ${seg.hoverStart} -19.61%, ${seg.hoverEnd} 131.76%)`,
                    }}
                    pointerEvents="none"
                  >
                    {seg.title}
                  </Button>
                </Box>
              )}

              <Box
                position="absolute"
                left={xText}
                top={yText}
                transform={`translate(-50%, -50%) rotate(${rotation}deg)`}
                textAlign="center"
                width={radius * 0.8}
                display="flex"
                flexDirection="column"
                alignItems="center"
                pointerEvents="none"
              >
                <Text
                  fontSize={`clamp(${size * 0.015}px, 1.5vw, ${
                    size * 0.025
                  }px)`}
                  fontWeight={600}
                  fontFamily="Poppins"
                  whiteSpace="normal"
                  overflowWrap="break-word"
                  color={"white"}
                >
                  {seg.subtitle}
                </Text>
                {seg.desc && (
                  <Text
                    fontSize={`clamp(${size * 0.01}px, 1vw, ${size * 0.018}px)`}
                    fontWeight={400}
                    fontFamily="Poppins"
                    mt="2px"
                    whiteSpace="normal"
                    overflowWrap="break-word"
                    color={"white"}
                  >
                    {seg.desc}
                  </Text>
                )}
              </Box>

              <Flex
                position="absolute"
                left={xInd}
                top={yInd}
                width={size * 0.12}
                height={size * 0.12}
                borderRadius="full"
                bg="#F4F4F2"
                transform="translate(-50%, -50%)"
                align="center"
                justify="center"
                cursor="pointer"
                onClick={() => onSegmentClick(seg)}
                onMouseEnter={() => setHoveredSegment(idx)}
                onMouseLeave={() => setHoveredSegment(null)}
              >
                <Flex
                  width={size * 0.1}
                  height={size * 0.1}
                  borderRadius="full"
                  bgGradient={
                    hoveredSegment === idx
                      ? `linear(to-b, ${seg.hoverStart}, ${seg.hoverEnd})`
                      : `linear(to-b, ${seg.gradientStart}, ${seg.gradientEnd})`
                  }
                  align="center"
                  justify="center"
                  fontSize={`clamp(${size * 0.01}px, 2.5vw, ${size * 0.045}px)`}
                  fontWeight="bold"
                  color="white"
                >
                  {idx + 1}
                </Flex>
              </Flex>
            </React.Fragment>
          );
        })}
      </Flex>
    </Box>
  );
}
